<template>
    <div class="kv-search-select">
        <div class="kv-search-header" v-if="title">
            <h3>{{ title }}</h3>
            <button
                v-if="showCloseButton"
                class="close-btn"
                @click="$emit('close')"
                type="button"
            >
                <!-- i18n-validator-ignore-next-line: universal close symbol -->
                ×
            </button>
        </div>

        <div class="kv-search-input-wrapper">
            <input
                type="text"
                class="kv-search-input"
                v-model="searchQuery"
                :placeholder="resolvedPlaceholder"
                @input="handleSearch"
                @focus="handleFocus"
                ref="searchInput"
            />
            <button
                v-if="(selectedItem || searchQuery) && showClearButton"
                class="clear-btn-inline"
                @click="clearSelection"
                type="button"
                :title="t('kv_search_select-clear')"
            >
                <!-- i18n-validator-ignore-next-line: universal close symbol -->
                ×
            </button>
        </div>

        <div
            class="kv-search-results"
            v-if="showOptions"
            v-click-outside="closeOptions"
        >
            <div class="kv-search-loading" v-if="loading">
                {{ t('kv_search_select-loading') }}
            </div>

            <div class="kv-search-empty" v-else-if="searchQuery.length >= minSearchLength && results.length === 0">
                {{ t('kv_search_select-empty') }}
            </div>

            <div class="kv-search-hint" v-else-if="searchQuery.length < minSearchLength && results.length === 0">
                {{ t('kv_search_select-hint', [minSearchLength]) }}
            </div>

            <button
                v-for="item in results"
                :key="item[valueField]"
                class="kv-search-item"
                @click="selectItem(item)"
                type="button"
            >
                <div class="item-primary">{{ getItemDisplay(item) }}</div>
                <div class="item-secondary" v-if="secondaryField && item[secondaryField]">
                    {{ item[secondaryField] }}
                </div>
            </button>

            <div class="kv-search-actions" v-if="showCloseButton">
                <button
                    type="button"
                    class="btn btn-sm btn-default"
                    @click="closeOptions"
                >
                    {{ t('kv_search_select-close') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import keviApi from '../../keviApi';

export default {
    name: 'kv-search-select',
    props: {
        title: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: null
        },
        apiEndpoint: {
            type: String,
            required: true
        },
        searchParam: {
            type: String,
            default: 'search'
        },
        value: {
            type: [Object, String, Number],
            default: null
        },
        displayField: {
            type: [String, Function],
            default: 'name'
        },
        valueField: {
            type: String,
            default: 'id'
        },
        secondaryField: {
            type: String,
            default: ''
        },
        showClearButton: {
            type: Boolean,
            default: true
        },
        showCloseButton: {
            type: Boolean,
            default: false
        },
        minSearchLength: {
            type: Number,
            default: 0
        },
        iconClass: {
            type: String,
            default: 'far fa-user'
        },
        additionalParams: {
            type: Object,
            default: () => ({})
        },
        fetchItemEndpoint: {
            type: [String, Boolean],
            default: null
        }
    },
    data() {
        return {
            searchQuery: '',
            searchTimeout: null,
            results: [],
            loading: false,
            selectedItem: null,
            showOptions: false
        };
    },
    computed: {
        displayText() {
            if (!this.selectedItem) return '';
            return this.getItemDisplay(this.selectedItem);
        },
        resolvedPlaceholder() {
            return this.placeholder !== null ? this.placeholder : this.t('kv_search_select-placeholder');
        }
    },
    mounted() {
        if (this.value && this.value !== null) {
            this.fetchSelectedItem();
        }
    },
    methods: {
        getItemDisplay(item) {
            if (typeof this.displayField === 'function') {
                return this.displayField(item);
            }
            return item[this.displayField] || '';
        },
        handleSearch() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }

            if (this.searchQuery.length >= this.minSearchLength) {
                this.loading = true;
                this.searchTimeout = setTimeout(() => {
                    this.fetchResults();
                }, 400);
            } else if (this.minSearchLength === 0) {
                this.loading = true;
                this.searchTimeout = setTimeout(() => {
                    this.fetchResults();
                }, 400);
            } else {
                this.results = [];
                this.loading = false;
            }
        },
        fetchResults() {
            const params = {
                ...this.additionalParams,
                [this.searchParam]: this.searchQuery,
                page: 1,
                limit: 20
            };

            const options = keviApi.getOptions(this.apiEndpoint, 'GET', params);

            this.$http(options)
                .then(response => {
                    this.results = response.body.data || response.body;
                    this.loading = false;
                })
                .catch(() => {
                    this.results = [];
                    this.loading = false;
                });
        },
        fetchSelectedItem() {
            if (typeof this.value === 'object' && this.value !== null) {
                this.selectedItem = this.value;
                this.searchQuery = this.getItemDisplay(this.selectedItem);
                this.$emit('select', this.selectedItem);
                return;
            }

            if (this.fetchItemEndpoint === false) {
                const params = {
                    ...this.additionalParams,
                    page: 1,
                    limit: 100
                };

                const options = keviApi.getOptions(this.apiEndpoint, 'GET', params);

                this.$http(options)
                    .then(response => {
                        const items = response.body.data || response.body;
                        const found = items.find(item => item[this.valueField] == this.value);
                        if (found) {
                            this.selectedItem = found;
                            this.searchQuery = this.getItemDisplay(this.selectedItem);
                            this.$emit('select', this.selectedItem);
                        }
                    })
                    .catch(() => {
                        this.selectedItem = null;
                    });
                return;
            }
            const baseUrl = this.fetchItemEndpoint || this.apiEndpoint;
            const finalEndpoint = `${baseUrl}/${this.value}`;
            const options = keviApi.getOptions(finalEndpoint, 'GET');

            this.$http(options)
                .then(response => {
                    this.selectedItem = response.body;
                    this.searchQuery = this.getItemDisplay(this.selectedItem);
                    this.$emit('select', this.selectedItem);
                })
                .catch(() => {
                    this.selectedItem = null;
                });
        },
        selectItem(item) {
            this.selectedItem = item;
            this.showOptions = false;
            this.searchQuery = this.getItemDisplay(item);
            this.results = [];
            this.$emit('select', item);
            this.$emit('input', item[this.valueField]);
        },
        handleFocus() {
            this.showOptions = true;
        },
        clearSelection() {
            this.selectedItem = null;
            this.showOptions = false;
            this.searchQuery = '';
            this.results = [];
            this.$emit('select', null);
            this.$emit('input', null);
        },
        closeOptions(event) {
            if (event && (event.target === this.$refs.searchInput || this.$refs.searchInput?.contains(event.target))) {
                return;
            }
            this.showOptions = false;
            if (!this.selectedItem) {
                this.searchQuery = '';
            } else {
                this.searchQuery = this.getItemDisplay(this.selectedItem);
            }
            this.results = [];
        },
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
    },
    watch: {
        value(newValue) {
            if (newValue !== this.selectedItem?.[this.valueField]) {
                if (newValue) {
                    this.fetchSelectedItem();
                } else {
                    this.selectedItem = null;
                }
            }
        },
        showOptions(newVal) {
            if (newVal) {
                if (this.minSearchLength === 0 && this.results.length === 0 && !this.loading) {
                    this.fetchResults();
                }

                if (this.$refs.searchInput) {
                    this.$nextTick(() => {
                        this.$refs.searchInput.focus();
                    });
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.kv-search-select {
    position: relative;
    width: 100%;
    box-sizing: border-box;

    .kv-search-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;

        h3 {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
            text-transform: uppercase;
        }

        .close-btn {
            background: none;
            border: none;
            font-size: 24px;
            color: #d32f2f;
            cursor: pointer;
            padding: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: opacity 0.2s;

            &:hover {
                opacity: 0.7;
            }
        }
    }

    .kv-search-input-wrapper {
        position: relative;
        display: flex;
        gap: 10px;
        margin-bottom: 10px;

        .kv-search-input {
            flex: 1;
            width: 100%;
            box-sizing: border-box;
            padding: 6px 40px 6px 15px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;

            &:focus {
                outline: none;
                border-color: #2AAA4C;
            }
        }

        .clear-btn-inline {
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            font-size: 20px;
            color: #d32f2f;
            cursor: pointer;
            padding: 0;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: opacity 0.2s;

            &:hover {
                opacity: 0.7;
            }
        }
    }

    .kv-search-results {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #ddd;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        max-height: 300px;
        overflow-y: auto;
        z-index: 1000;
        margin-top: 5px;

        .kv-search-loading,
        .kv-search-empty,
        .kv-search-hint {
            padding: 15px;
            text-align: center;
            color: #666;
            font-size: 14px;
        }

        .kv-search-item {
            width: 100%;
            padding: 12px 15px;
            border: none;
            border-bottom: 1px solid #f0f0f0;
            background: white;
            text-align: left;
            cursor: pointer;
            transition: background 0.2s;

            &:hover {
                background: #f5f5f5;
            }

            &:last-child {
                border-bottom: none;
            }

            .item-primary {
                font-weight: 500;
                margin-bottom: 4px;
            }

            .item-secondary {
                font-size: 13px;
                color: #666;
            }
        }

        .kv-search-actions {
            padding: 10px;
            border-top: 1px solid #f0f0f0;
            text-align: center;
        }
    }
}
</style>
